import React, { useMemo, useCallback } from 'react';

import gql from 'graphql-tag';

import { Loading, Button, ControlMenu } from 'components';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { useQuery } from '@apollo/client';
import { cloneDeep } from '@apollo/client/utilities';

import { getChannelIntegrations } from 'lane-shared/graphql/query';
import { simpleDate, fromNow } from 'lane-shared/helpers/formatters';
import { getIntegrationDisplayName } from 'lane-shared/helpers/integrations/getIntegrationDefinition';
import { getIntegrationDefinition } from 'application-types';
import { byName } from 'lane-shared/helpers/sort';
import { useFlag, useSetContextualLDAttributes } from 'lane-shared/hooks';
import { FeatureFlag } from 'constants-flags';
import { IntegrationProviderEnum } from 'constants-integrations';
import { convertTo62 } from 'uuid-encoding';

import { AdminPage } from 'components/layout';
import { H5 } from 'components/typography';

import useChannelAdminContext from 'hooks/useChannelAdminContext';
import {
  useNotificationIntegrationToggle,
  notificationIntegrationProviders,
  ChannelIntegrationRecord,
  AvailableIntegrationRecord,
} from './hooks/useNotificationIntegrationToggle';

const notificationProviderNames = new Set<string>(
  notificationIntegrationProviders
);

const availableIntegrationsQuery = gql`
  query getAvailableIntegrations($channelId: UUID!) {
    availableIntegrations(channelId: $channelId) {
      _id
      name
    }
  }
`;

export default function ChannelIntegrations() {
  const { channel } = useChannelAdminContext();
  const { t } = useTranslation();

  useSetContextualLDAttributes({
    focusedChannelId: channel?._id
      ? convertTo62(channel._id)
      : undefined,
  });

  const deleteIntegrationFeatureEnabled = useFlag(
    FeatureFlag.DeleteChannelIntegrationAccess,
    true
  );

  const isYardiFlagEnabled = useFlag(FeatureFlag.YardiConfigSetting, false);
  const isYardiCommercialFlagEnabled = useFlag(
    FeatureFlag.YardiCommercialConfigSetting,
    false
  );
  const isEntrataFlagEnabled = useFlag(FeatureFlag.EntrataConfigSetting, false);
  const isRealPageFlagEnabled = useFlag(
    FeatureFlag.RealPageConfigSetting,
    false
  );
  const isCohesiveEnabled = useFlag(
    FeatureFlag.CohesiveNotificationIntegrations,
    false
  );

  const {
    data,
    loading,
    called,
    refetch: refetchChannelIntegrations,
  } = useQuery<{ channel: { integrations: ChannelIntegrationRecord[] } }>(
    getChannelIntegrations,
    {
      skip: !channel?._id,
      variables: { id: channel?._id },
      fetchPolicy: 'network-only',
    }
  );
  const {
    data: availableIntegrationsData,
    refetch: refetchAvailableIntegrations,
  } = useQuery<{ availableIntegrations: AvailableIntegrationRecord[] }>(
    availableIntegrationsQuery,
    {
      skip: !channel?._id || !isCohesiveEnabled,
      variables: { channelId: channel?._id },
      fetchPolicy: 'network-only',
    }
  );

  const integrations = useMemo<ChannelIntegrationRecord[]>(
    () =>
      cloneDeep(data?.channel?.integrations || []).sort((a, b) =>
        byName(a.integration, b.integration)
      ),
    [data?.channel?.integrations]
  );

  const hasNoChannelIntegrations =
    called && !loading && integrations.length === 0;

  const handleSettled = useCallback(async () => {
    if (!isCohesiveEnabled) {
      return;
    }

    await Promise.all([
      refetchChannelIntegrations(),
      refetchAvailableIntegrations(),
    ]);
  }, [
    isCohesiveEnabled,
    refetchChannelIntegrations,
    refetchAvailableIntegrations,
  ]);

  const { notificationIntegrations, toggleIntegration, isToggling } =
    useNotificationIntegrationToggle({
      channelId: channel?._id,
      integrations,
      availableIntegrations:
        availableIntegrationsData?.availableIntegrations || [],
      onSettled: handleSettled,
    });

  const filterIntegrations = (
    integrations: ChannelIntegrationRecord[]
  ): ChannelIntegrationRecord[] => {
    return integrations.filter(integration => {
      if (notificationProviderNames.has(integration.integration.name)) {
        return false;
      }

      if (
        !isYardiFlagEnabled &&
        integration.integration.name === IntegrationProviderEnum.Yardi
      ) {
        return false;
      }

      if (
        !isYardiCommercialFlagEnabled &&
        integration.integration.name === IntegrationProviderEnum.YardiCommercial
      ) {
        return false;
      }

      if (
        !isRealPageFlagEnabled &&
        integration.integration.name === IntegrationProviderEnum.RealPage
      ) {
        return false;
      }

      if (
        !isEntrataFlagEnabled &&
        integration.integration.name === IntegrationProviderEnum.Entrata
      ) {
        return false;
      }

      return true;
    });
  };

  return (
    <AdminPage>
      <ControlMenu>
        <hr />
        <Link to="integrations/new">
          <Button testId="addIntegration" variant="contained">
            {t`web.admin.channel.integrations.add`}
          </Button>
        </Link>
      </ControlMenu>

      {loading && <Loading />}

      {hasNoChannelIntegrations && (
        <H5>{t('web.admin.channel.integrations.empty')}</H5>
      )}

      {integrations.length > 0 && (
        <table data-test="integrationsTable">
          <thead>
            <tr>
              <th>{t`web.admin.channel.integrations.table.name`}</th>
              <th>{t`web.admin.channel.integrations.table.type`}</th>
              <th>{t`web.admin.channel.integrations.table.created`}</th>
              <th>{t`web.admin.channel.integrations.table.updated`}</th>
              {deleteIntegrationFeatureEnabled && (
                <th>{t`web.admin.channel.integrations.table.deleted`}</th>
              )}
            </tr>
          </thead>

          <tbody>
            {filterIntegrations(integrations).map(integration => (
              <tr key={integration._id}>
                <td>
                  <Link to={`integrations/${integration._id}/edit`}>
                    {getIntegrationDisplayName(integration)}
                  </Link>
                </td>
                <td>{integration.integration.type}</td>
                <td>{simpleDate(integration._created)}</td>
                <td>{fromNow(integration._updated)}</td>
                {deleteIntegrationFeatureEnabled && (
                  <td>{simpleDate(integration.deletedAt)}</td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {isCohesiveEnabled && notificationIntegrations.length > 0 && (
        <>
          <H5>{t`web.admin.channel.integrations.notification.title`}</H5>
          <table data-test="notificationIntegrationsTable">
            <thead>
              <tr>
                <th>{t`web.admin.channel.integrations.table.name`}</th>
                <th>{t`web.admin.channel.integrations.notification.table.status`}</th>
                <th>{t`web.admin.channel.integrations.notification.table.action`}</th>
              </tr>
            </thead>
            <tbody>
              {notificationIntegrations.map(notificationIntegration => (
                <tr key={notificationIntegration.provider}>
                  <td>
                    {
                      getIntegrationDefinition(notificationIntegration.provider)
                        .friendlyName
                    }
                  </td>
                  <td>
                    {notificationIntegration.isEnabled
                      ? t`web.admin.channel.integrations.status.active`
                      : t`web.admin.channel.integrations.status.inactive`}
                  </td>
                  <td>
                    <Button
                      variant={
                        notificationIntegration.isEnabled
                          ? 'outlined'
                          : 'contained'
                      }
                      disabled={!notificationIntegration.integrationId}
                      loading={isToggling[notificationIntegration.provider]}
                      onClick={() =>
                        toggleIntegration(notificationIntegration.provider)
                      }
                    >
                      {notificationIntegration.isEnabled
                        ? t`web.admin.channel.integrations.button.deactivate`
                        : t`web.admin.channel.integrations.button.activate`}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </AdminPage>
  );
}
